import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "../../components/Modal";
import InputField from "../../components/InputField";
import { Alert, Box, Button, Typography } from "@mui/material";
import { useApiRequest } from "../../hooks/useApiRequest";
import { PasswordInput } from "../../components/PasswordInput";
import { Tenant } from "../../types/tenant";
import CheckboxField from "../../components/CheckboxField";

type NewEditModalProps = {
  isOpen: boolean;
  onSaveSuccessful: () => void;
  onClose: () => void;
  tenant: Partial<Tenant> | null;
  isNew: boolean;
  newType: Tenant["tenantType"];
  isAssigningOwner?: boolean;
};

const defaultFormData = {
  tenantName: "",
  userFirstName: "",
  userLastName: "",
  userPhoneNumber: "",
  userEmail: "",
  userPassword: "",
  sendInvite: false,
};

type FlatTenant = {
  tenantGuid: string;
  tenantType: Tenant["tenantType"] | "";
  tenantName: string;
  userGuid: string;
  userFirstName: string;
  userLastName: string;
  userPhoneNumber: string;
  userEmail: string;
  userPassword: string;
  sendInvite: boolean;
};

export const NewEditModal: React.FC<NewEditModalProps> = ({
  isOpen,
  onSaveSuccessful,
  onClose,
  tenant,
  isNew,
  newType,
  isAssigningOwner,
}) => {
  const [formData, setFormData] =
    useState<Partial<FlatTenant>>(defaultFormData);
  const { data, loading, request, status, errorMessage } =
    useApiRequest<Tenant>(true, "tenant");

  const isPhoneNumberValid = useMemo(
    () => !!formData.userPhoneNumber?.match(/^\d{10}$/),
    [formData.userPhoneNumber]
  );
  const isValidEmail = useMemo(
    () => formData.userEmail?.match(/[^@]+@[^@]+\.[^@]+/),
    [formData.userEmail]
  );

  const handleChange =
    (prop: keyof typeof formData) =>
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setFormData({ ...formData, [prop]: event.target.value });
    };

  useEffect(() => {
    if (isNew) {
      setFormData({ ...defaultFormData, tenantType: newType });
    } else if (tenant) {
      setFormData({
        ...tenant,
        userFirstName: tenant.ownerUser?.userFirstName,
        userLastName: tenant.ownerUser?.userLastName,
        userEmail: tenant.ownerUser?.userEmail,
        userPhoneNumber: tenant.ownerUser?.userPhoneNumber,
        userPassword: "",
      });
    }
  }, [tenant, isNew, newType]);

  const isValid = useCallback(() => {
    if (isNew || isAssigningOwner) {
      return (
        formData.userFirstName &&
        formData.userLastName &&
        isPhoneNumberValid &&
        isValidEmail &&
        formData.userPassword
      );
    } else {
      return (
        formData.userFirstName &&
        formData.userLastName &&
        isPhoneNumberValid &&
        isValidEmail
      );
    }
  }, [formData, isNew, isAssigningOwner, isPhoneNumberValid, isValidEmail]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (newType === "r" && isAssigningOwner) {
      request(`/retailer/assign`, {
        method: "POST",
        data: {
          tenantGuid: tenant?.tenantGuid,
          userFirstName: formData.userFirstName,
          userLastName: formData.userLastName,
          userPhoneNumber: formData.userPhoneNumber,
          userEmail: formData.userEmail,
          userPassword: formData.userPassword,
          sendInvite: formData.sendInvite,
        },
      });
    } else if (isNew) {
      // Create new user
      request("/tenants/add", {
        method: "POST",
        data: {
          ...formData,
          userRole: "sysadmin",
        },
      });
    } else {
      request(`/tenants/${tenant?.tenantGuid}/edit`, {
        method: "POST",
        data: {
          ...(formData.tenantName ? { tenantName: formData.tenantName } : {}),
          ...(formData.userFirstName
            ? { userFirstName: formData.userFirstName }
            : {}),
          ...(formData.userLastName
            ? { userLastName: formData.userLastName }
            : {}),
          ...(formData.userPhoneNumber
            ? { userPhoneNumber: formData.userPhoneNumber }
            : {}),
          ...(formData.userEmail ? { userEmail: formData.userEmail } : {}),
          ...(formData.userPassword
            ? { userPassword: formData.userPassword }
            : {}),
        },
      });
    }
  };

  useEffect(() => {
    if (data && status === "ok" && loading === false) {
      onSaveSuccessful();
    }
  }, [data, onSaveSuccessful, status, loading]);

  return (
    <Modal
      heading={`${isAssigningOwner ? "Assign " : isNew ? "New" : "Edit"} ${
        isAssigningOwner
          ? "Owner"
          : newType === "m"
          ? "Manufacturer"
          : "Retailer"
      }`}
      isOpen={isOpen}
      onClose={onClose}
    >
      {errorMessage && (
        <Alert severity="error" sx={{ marginBottom: 2 }}>
          {errorMessage}
        </Alert>
      )}
      <form onSubmit={onSubmit}>
        <InputField
          fullWidth
          disabled={newType === "r" && isAssigningOwner}
          label={`${newType === "m" ? "Manufacturer" : "Retailer"} Name`}
          value={formData.tenantName}
          onChange={handleChange("tenantName")}
          sx={{ marginBottom: 2 }}
        />
        {isAssigningOwner ? (
          <Typography variant="body2" fontWeight={800} sx={{ marginBottom: 2 }}>
            Owner
          </Typography>
        ) : null}
        <Box sx={{ display: "flex", gap: 2, marginBottom: 2 }}>
          <InputField
            fullWidth
            label="First Name"
            required
            value={formData.userFirstName}
            onChange={handleChange("userFirstName")}
          />
          <InputField
            fullWidth
            label="Last Name"
            required
            value={formData.userLastName}
            onChange={handleChange("userLastName")}
          />
        </Box>
        <InputField
          fullWidth
          label="Phone"
          required
          error={!!formData.userPhoneNumber && !isPhoneNumberValid}
          value={formData.userPhoneNumber}
          onChange={handleChange("userPhoneNumber")}
          sx={{ marginBottom: 2 }}
        />
        <InputField
          fullWidth
          label="Email"
          required
          fixFor="username"
          error={!!formData.userEmail && !isValidEmail}
          value={formData.userEmail}
          onChange={handleChange("userEmail")}
          sx={{ marginBottom: 2 }}
        />
        <PasswordInput
          fullWidth
          fixFor="new-password"
          label="Password"
          required
          value={formData.userPassword || ""}
          onChange={handleChange("userPassword")}
          sx={{ marginBottom: 2 }}
        />
        {isAssigningOwner && (
          <CheckboxField
            label="Send invite email"
            onChange={(e) =>
              setFormData({ ...formData, sendInvite: e.target.checked })
            }
            checked={formData.sendInvite}
          />
        )}
        <Box sx={{ mt: 2, display: "flex", justifyContent: "end" }}>
          <Button variant="outlined" onClick={onClose} sx={{ marginRight: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            type="submit"
            color="primary"
            disabled={!isValid()}
          >
            Save
          </Button>
        </Box>
      </form>
    </Modal>
  );
};
